import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faBox, faBoxCheck } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Box, Button, Columns, LoadingModule, SectionDivider,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import type { DefaultContext } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import useCampaign from '@hooks/useCampaign'
import useCampaignItemsAndOrders from '@hooks/useCampaignItemsAndOrders'
import useOrder, { generateUrls } from '@hooks/useOrder'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageHeader from '@components/PageHeader'

import type { CampaignModel } from '@models/campaign'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'
import OrderItemTasks from '@sections/Client/packs/Campaign/screens/Orders/components/OrderItemTasks'
import OrderScreenFooter from '@sections/Client/packs/Campaign/screens/Orders/components/OrderScreenFooter'

import SimpleNoticeBox from '@components/SimpleNoticeBox/SimpleNoticeBox'

import type { PageContextPayload } from '../../index'

import OrderNewItemButton from '../../../../components/OrderNewItemButton'

import OrdersBlankState from '../OrdersBlankState'
import PendingOrder from '../PendingOrder'
import PendingOrderTools from '../PendingOrderTools'
import PublicationOrder from '../PublicationOrder'

type QueryParams = { cacheKey?: string, redirect?: string}

interface PageContentProps {
  campaign: CampaignModel,
}

const PageContent = (props: PageContentProps) => {
  const { campaign } = props
  const { address } = campaign || {}

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { cacheKey, redirect }: QueryParams = parsePermittedQueryParams(getQueryParamsFromHash(), [
    'cacheKey', 'redirect',
  ])

  const campaignItemsAndOrdersPayload = useCampaignItemsAndOrders({
    campaign,
    performHttpRequests: true,
    requestOptions: {
      cache_key: cacheKey,
      order_by: 'id desc',
      ...defaultRequestOptions.artwork,
      ...defaultRequestOptions.artworkPage,
      ...defaultRequestOptions.artworkReview,
      ...defaultRequestOptions.attachment,
      ...defaultRequestOptions.order,
      ...defaultRequestOptions.orderItem,
      ...defaultRequestOptions.orderItemTask,
      ...defaultRequestOptions.publication,
      ...defaultRequestOptions.publicationEdition,
    },
  })

  const {
    canGenerateSchedule,
    groupedOrders: {
      pending: pendingOrders,
      publication: publicationOrders,
    },
    loading,
    orderLimitReached,
  } = campaignItemsAndOrdersPayload

  const hasOrders = !!pendingOrders.length || !!publicationOrders.length

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      showBulkShareArtworksModal,
      showGenerateCampaignScheduleModal,
    },
  } = pageContext

  const { approvedCampaignOrdersUrl, pendingCampaignOrdersUrl } = generateUrls({ campaign_id: campaign?.id })

  const orderPayload = useOrder({ agency_id: campaign.agency_id, campaign_id: campaign.id })
  const { callbacks: { addNewOrder } } = orderPayload

  const campaignPayload = useCampaign({ id: campaign.id })
  const { brand, isPropertyMarketing, vendorAmlComplete } = campaignPayload

  const compliantWithAML = brand?.options?.aml_compliance_enforcement ?? false

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Pending Orders</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <>
            <OrderNewItemButton
              callbacks={{ addNewOrder }}
              campaignId={campaign.id}
              orderLimitReached={orderLimitReached}
            />

            {redirect && (
              <Button
                // @ts-expect-error TS2322
                as="a"
                buttonStyle="secondaryUtility"
                href={redirect}
                marginLeft="medium"
                size="medium"
                width="auto"
              >
                Back
              </Button>
            )}
          </>
        )}
        activeTabBarItemKey="pending"
        nestedNavigation
        tabBarItems={[
          {
            href: pendingCampaignOrdersUrl,
            icon: faBox,
            key: 'pending',
            title: 'Pending',
          },
          {
            href: approvedCampaignOrdersUrl,
            icon: faBoxCheck,
            key: 'approved',
            title: 'Approved',
          },
        ]}
        title="Orders"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.orders.banner" />

            <LoadingModule loading={loading} times={3} />

            {compliantWithAML && isPropertyMarketing && !vendorAmlComplete && (
              <SimpleNoticeBox
                title="Order creation unavailable due to AML compliance requirements"
                paragraph="Ordering is unavailable until AML compliance has been confirmed for this vendor."
              />
            )}

            {!loading && !hasOrders && <OrdersBlankState />}

            {/* Publication Orders */}
            {!loading && !!publicationOrders.length && (
              <>
                <SectionDivider boxProps={{ marginBottom: 'large' }}>Publications</SectionDivider>
                {/*
                 // @ts-expect-error TS2339 */}
                {publicationOrders.map(order => <PublicationOrder key={order.id} order={order} />)}
              </>
            )}
            {/* End Publication Orders */}

            {/* Pending Orders */}
            {!loading && !!pendingOrders.length && (
              <>
                <SectionDivider boxProps={{ marginBottom: 'large' }}>Pending</SectionDivider>
                {/*
                 // @ts-expect-error TS2339 */}
                {pendingOrders.map(order => <PendingOrder key={order.id} order={order} />)}
              </>
            )}
            {/* End Pending Orders */}

            {/* Approved Orders */}
            <>
              <SectionDivider boxProps={{ marginBottom: 'large' }}>Approved</SectionDivider>
              <OrderScreenFooter href={approvedCampaignOrdersUrl}>
                <FontAwesomeIcon icon={faBoxCheck} /> Approved Orders
              </OrderScreenFooter>
            </>
            {/* End Approved Orders */}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.orders.sidebar" />

            <PendingOrderTools
              callbacks={{
                showBulkShareArtworksModal,
                showGenerateCampaignScheduleModal,
              }}
              canGenerateSchedule={canGenerateSchedule}
            />

            <OrderItemTasks campaign={campaign} />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
